import { TAU } from './math';

export type ShapeKind = 'circle' | 'square' | 'triangle' | 'hexagon' | 'star';

export const SHAPE_OPTIONS: { value: ShapeKind | 'mixed'; label: string }[] = [
  { value: 'circle', label: 'Kreis' },
  { value: 'square', label: 'Quadrat' },
  { value: 'triangle', label: 'Dreieck' },
  { value: 'hexagon', label: 'Hexagon' },
  { value: 'star', label: 'Stern' },
  { value: 'mixed', label: 'Zufallsmix' },
];

export const SHAPE_KINDS: ShapeKind[] = ['circle', 'square', 'triangle', 'hexagon', 'star'];

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | Path2D;

/**
 * Appends a regular shape inscribed in a circle of radius `r` to the current path.
 *
 * Everything is expressed on the circumscribed circle so that swapping the shape of a
 * packing keeps the layout identical — squares and hexagons sit exactly where their
 * circles were, rather than shrinking the composition.
 */
export function traceShape(
  path: Ctx,
  kind: ShapeKind,
  x: number,
  y: number,
  r: number,
  rotation = 0,
  starPoints = 5,
  starInner = 0.45,
): void {
  if (kind === 'circle') {
    path.moveTo(x + r, y);
    path.arc(x, y, r, 0, TAU);
    return;
  }

  if (kind === 'star') {
    const points = Math.max(3, Math.round(starPoints));
    for (let i = 0; i < points * 2; i++) {
      const radius = i % 2 === 0 ? r : r * starInner;
      const a = rotation - Math.PI / 2 + (i * Math.PI) / points;
      const px = x + Math.cos(a) * radius;
      const py = y + Math.sin(a) * radius;
      if (i === 0) path.moveTo(px, py);
      else path.lineTo(px, py);
    }
    path.closePath();
    return;
  }

  const sides = kind === 'square' ? 4 : kind === 'triangle' ? 3 : 6;
  // Squares read as squares only when axis-aligned; the others look best point-up.
  const offset = kind === 'square' ? Math.PI / 4 : -Math.PI / 2;
  for (let i = 0; i < sides; i++) {
    const a = rotation + offset + (i * TAU) / sides;
    const px = x + Math.cos(a) * r;
    const py = y + Math.sin(a) * r;
    if (i === 0) path.moveTo(px, py);
    else path.lineTo(px, py);
  }
  path.closePath();
}
